import { FeatureCart } from '@mdlbeasts/nofomo.ui.web';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { isAfter } from 'date-fns';
import { type ReactNode, useMemo, useState } from 'react';

import { useLocale } from 'hooks/useLocale';
import { useTranslatedText } from 'hooks/useTranslatedText';
import type { Ticket } from 'restful/tickets';
import { useTrackViewItem } from 'services/datalayer/ecomm/useTrackViewItem';
import { TicketStatusFragment } from 'types/api/dato-operations.types';

import Icon from 'components/@icons/Icon';
import { useTicketInfo } from 'components/@tickets/hooks';
import TicketPrice from 'components/@tickets/TicketPrice';

interface Props {
  ticket: Ticket;
  children: ReactNode;
  status?: TicketStatusFragment;
}

const TicketItemAccordion = ({ ticket, status, children }: Props) => {
  const { locale } = useLocale();
  const localize = useTranslatedText();
  const { originalPrice, discountedPrice, isSoldOut } = useTicketInfo(
    ticket.id,
  );
  const handleViewItem = useTrackViewItem();
  const [expanded, setExpanded] = useState(false);

  const description = localize(ticket.description);

  const handleTrackViewChange = (nextExpanded: boolean) => {
    setExpanded(nextExpanded);
    if (nextExpanded) handleViewItem(ticket);
  };

  const isStatusActive = useMemo(() => {
    if (!status) {
      return false;
    }

    if (status.statusType === 'schedule' && status?.schedule) {
      //Check if the date has passed
      if (isAfter(new Date(), new Date(status?.schedule))) {
        return false;
      }
    }

    return true;
  }, [status]);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, nextExpanded) => handleTrackViewChange(nextExpanded)}
    >
      <AccordionSummary
        aria-controls={`panel${ticket.id}-content`}
        id={`panel${ticket.id}-header`}
        disabled={!description}
        sx={{
          [`& .${accordionSummaryClasses.content}`]: {
            margin: 0,
            width: '100%',
          },
        }}
      >
        <Stack
          sx={{
            flexGrow: 1,
            gap: 2,
            width: '100%',
            minWidth: 0,
          }}
        >
          <FeatureCart.TicketItem.Promo
            content={localize(ticket.promoMessage)}
            locale={locale}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={(theme) => ({
                display: 'flex',
                color: 'grey.400',
                flexShrink: 0,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
              })}
            >
              <Icon name="ChevronDown" />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                gap: 2,
                ms: 1,
              }}
            >
              <Stack
                sx={{
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="small"
                  component="h3"
                  sx={{
                    fontWeight: 'bold',
                    whiteSpace: 'normal',
                  }}
                >
                  {localize(ticket.name)}
                </Typography>

                <TicketPrice
                  originalPrice={originalPrice}
                  discountedPrice={discountedPrice}
                  color="text.secondary"
                />
              </Stack>

              {!isStatusActive && !isSoldOut && <>{children}</>}
            </Box>
          </Box>

          <FeatureCart.TicketItem.Status
            color={status?.color?.hex}
            content={status?.content}
            type={
              status?.statusType as FeatureCart.TicketItem.StatusProps['type']
            }
            schedule={status?.schedule}
            soldOut={isSoldOut}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {!!ticket.perks?.length && (
          <Stack
            sx={{
              gap: 2,
              mb: 2,
            }}
          >
            {ticket.perks.map((perk) => (
              <Box
                key={perk.id}
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <Icon name="Check" fontSize={16} color="global01.400" />
                <Typography
                  variant="small"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {localize(perk.name)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        <Typography
          variant="small"
          sx={{
            color: 'text.secondary',
          }}
        >
          {description}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export default TicketItemAccordion;
